import React, { useState, useEffect } from 'react';
import { useTranslate } from '@im/react-localization';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { StyledText } from '../../../../components/StyledTypography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import SearchBarComp from 'src/components/SearchBar/SearchBar';
import FormGroup from '@mui/material/FormGroup';
import debounce from '@mui/material/utils/debounce';
import {
  DeatilsResponse,
  ResellerSearchResponse,
  useResellerSearch,
} from '../../hooks/deal-details';
import {
  useDefaultReseller,
  useEndUserSearch,
} from '../../hooks/exceptionDeal';
import DrawerWrapper from 'src/components/Drawer/DrawerWrapper';
import { useSearchParams } from 'react-router-dom';
import DefaultDialog from '../DefaultDialog';
import { useAccount } from '@azure/msal-react';

const DEBOUCE_DELAY = 500;
const RESELLER_NUMBER_MAX_LENGTH = 8;
const RESELLER_NUMBER_PATTERN = /^[a-zA-Z0-9]*$/;

export default function DealDeatailsPageEditReseller(
  props: Readonly<{
    details: DeatilsResponse | undefined;
    handleClose: () => void;
    role: boolean;
  }>
) {
  const translate = useTranslate();
  const user_id = useAccount()?.username ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const acopId =
    searchParams.get('edAcopEndUserId') ?? props.details?.end_user_number;
  const countryCode = searchParams.get('countryCode');
  const [resellerInput, setResellerInput] = useState({
    resellerName: '',
    resellerNumber: '',
  });
  const [makeDefault, setMakeDefault] = useState<boolean>(false);
  const [selectedReseller, setSelectedReseller] = useState({
    resellerName: '',
    resellerNumber: '',
  });
  const [defaultWarning, setDefaultWarning] = useState<number | null>(null);
  const [resellerNumberValidationMessage, setResellerNumberValidationMessage] =
    useState('');

  const resellerSearch = useResellerSearch({
    reseller_name: resellerInput.resellerName,
    reseller_no: resellerInput.resellerNumber,
  });
  const defaultReseller = useDefaultReseller({
    dealnumber: props.details?.deal_number ?? '',
    dealversion: props.details?.version ?? 0,
    reseller_name: props.details?.reseller_name ?? '',
    reseller_no: selectedReseller.resellerNumber,
    user_id,
    vendor_id: props.details?.vendor_id ?? 0,
    vendor_program_id: props.details?.vendor_program_id ?? 0,
  });

  const endUserSearch = useEndUserSearch({
    'IM-CustomerNumber': selectedReseller.resellerNumber,
    countryCode: countryCode ?? '',
  });

  const error = defaultReseller.error || endUserSearch.error;

  const renderResellerOption = (option: ResellerSearchResponse) => (
    <Stack
      alignItems={'center'}
      direction={'row'}
      justifyContent={'space-between'}
      width={'100%'}
    >
      <StyledText>{option.reseller_name}</StyledText>
      <StyledText>{option.reseller_no}</StyledText>
    </Stack>
  );
  const getResellerOptionLabel = (option: ResellerSearchResponse) =>
    typeof option === 'string'
      ? option
      : `${option.reseller_name} ~ ${option.reseller_no}`;

  const handleResellerOptionSelected = (option?: any) => {
    setSelectedReseller({
      resellerName: option?.reseller_name || '',
      resellerNumber: option?.reseller_no || '',
    });
    !option && setMakeDefault(false);
  };

  const getResellerNumberValidationMessage = (value: string) =>
    value.length > RESELLER_NUMBER_MAX_LENGTH ||
    !RESELLER_NUMBER_PATTERN.test(value)
      ? translate('VendorDeals.ResellerSearchByNoValidation')
      : '';

  const handleResellerNumberInputChange = (value: string) => {
    if (value.includes('~')) return;

    const validationMessage = getResellerNumberValidationMessage(value);
    setResellerNumberValidationMessage(validationMessage);
    setResellerInput((currentInput) => ({
      ...currentInput,
      resellerNumber: validationMessage ? '' : value,
    }));

    if (validationMessage) handleResellerOptionSelected();
  };

  const handleResellerNumberClear = () => {
    setResellerNumberValidationMessage('');
    setResellerInput((currentInput) => ({
      ...currentInput,
      resellerNumber: '',
    }));
    handleResellerOptionSelected();
  };

  const checkDefaultReseller = () => {
    defaultReseller
      .mutateAsync(false)
      .then(
        (res) =>
          [400, 409].includes(res.statusCode) &&
          setDefaultWarning(res.statusCode)
      )
      .catch(() => undefined);
  };

  useEffect(() => {
    makeDefault && selectedReseller.resellerNumber && checkDefaultReseller();
  }, [selectedReseller, makeDefault]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOverrideDefaultConfirmation = (result: boolean) => {
    setDefaultWarning(null);
    setMakeDefault(result);
  };

  const onSaveClick = () => {
    if (selectedReseller.resellerNumber !== '') {
      (makeDefault
        ? defaultReseller.mutateAsync(makeDefault)
        : Promise.resolve()
      )
        .then(() =>
          acopId
            ? endUserSearch
                .mutateAsync({
                  acopEndUserId: Number(acopId),
                  searchType: 'exact',
                })
                .then((res) => {
                  if (!res.length) {
                    searchParams.set('edAcopEndUserId', '');
                    setSearchParams(searchParams, { replace: true });
                  }
                })
            : Promise.resolve()
        )
        .then(() => {
          searchParams.set('edResellerId', selectedReseller.resellerNumber);
          setSearchParams(searchParams, { replace: true });
          props.handleClose();
        })
        .catch((err) => undefined);
    }
  };

  return (
    <DrawerWrapper
      disableSave={
        selectedReseller.resellerName === '' ||
        selectedReseller.resellerNumber === ''
      }
      exception={true}
      handleClose={props.handleClose}
      headerText={translate('VendorDeals.ResellerTitle')}
      loading={defaultReseller.isLoading || endUserSearch.isLoading}
      onSaveClick={onSaveClick}
    >
      <Stack p={2} spacing={1}>
        {error && (
          <Alert severity="error" variant="standard">
            {JSON.stringify(error)}
          </Alert>
        )}
        <FormGroup>
          <StyledText>
            {translate('VendorDeals.ResellerSearchByNameLabel')}
          </StyledText>
          <SearchBarComp
            autoCompleteDataTestId="gds-vp-ed-reseller-name-searchbar"
            exception={true}
            getOptionLabel={getResellerOptionLabel}
            onClear={handleResellerOptionSelected}
            onInputChange={debounce((value: string) => {
              if (!value.includes('~'))
                setResellerInput({ ...resellerInput, resellerName: value });
            }, DEBOUCE_DELAY)}
            onOptionSelected={handleResellerOptionSelected}
            options={resellerSearch.data ?? []}
            placeHolder={translate(
              'VendorDeals.ResellerSearchByNamePlaceholder'
            )}
            renderOption={renderResellerOption}
            textFieldDataTestId="gds-vp-ed-reseller-name-textfield"
          />
        </FormGroup>
        <FormGroup>
          <StyledText>
            {translate('VendorDeals.ResellerSearchByNoLabel')}
          </StyledText>
          <SearchBarComp
            autoCompleteDataTestId="gds-vp-ed-reseller-number-searchbar"
            error={Boolean(resellerNumberValidationMessage)}
            exception={true}
            getOptionLabel={getResellerOptionLabel}
            helperText={resellerNumberValidationMessage}
            onClear={handleResellerNumberClear}
            onInputChange={debounce((value: string) => {
              handleResellerNumberInputChange(value);
            }, DEBOUCE_DELAY)}
            onOptionSelected={handleResellerOptionSelected}
            options={resellerSearch.data ?? []}
            placeHolder={translate('VendorDeals.ResellerSearchByNoPlaceholder')}
            renderOption={renderResellerOption}
            textFieldDataTestId="gds-vp-ed-reseller-number-textfield"
          />
        </FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={makeDefault}
              disabled={!selectedReseller.resellerNumber}
              onChange={(_, checked) => setMakeDefault(checked)}
            />
          }
          label={translate('VendorDeals.ResellerMakeDefaultLabel')}
        />
        {!!defaultWarning && (
          <DefaultDialog
            defaultWarning={defaultWarning}
            handleOverrideDefaultConfirmation={
              handleOverrideDefaultConfirmation
            }
            keyName="Reseller"
            keyValue={selectedReseller.resellerNumber}
          />
        )}
      </Stack>
    </DrawerWrapper>
  );
}
